import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Undo2 } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_PRE_BATCH_LABELS } from '../../constants/pcmpMis.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useRollbackPcmpUrgentBatch } from '../../hooks/usePcmpUrgentBatchActions';
import type { PcmpUrgentRollbackResult } from '../../types/pcmpUrgent.types';
import { PCMP_CHANNELS } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';

// --- Staged batch form (existing behaviour) ---

const stagedFormSchema = z.object({
  scope: z.enum(['SELECTED', 'WHOLE']),
  comment: z.string().trim().max(PCMP_MKT_COMMENT_MAX),
});

type StagedFormValues = z.infer<typeof stagedFormSchema>;

// --- Published batch form (LDS-823) ---

const ROLLBACK_REASONS = [
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'CLOSED', label: 'Closed / Rejected' },
  { value: 'ACCOUNTS_ACTED', label: 'Accounts Already Acted' },
  { value: 'NOT_APPROVED', label: 'Not Approved' },
  { value: 'NOT_VERIFIED', label: 'Not Verified' },
] as const;

const publishedFormSchema = z.object({
  paymentSource: z.enum(PCMP_CHANNELS),
  reason: z.string().min(1, 'Pick a reason.'),
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type PublishedFormValues = z.infer<typeof publishedFormSchema>;

// --- Shared types ---

export interface PcmpUrgentRollbackTarget {
  batchNo: number;
  proposalIds: number[];
  selectedAmount: number;
  wholeCount: number;
  isPublished: boolean;
}

interface PcmpUrgentRollbackDialogProps {
  target: PcmpUrgentRollbackTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  onRolledBack: () => void;
  onClose: () => void;
}

export function PcmpUrgentRollbackDialog(props: PcmpUrgentRollbackDialogProps) {
  if (props.target.isPublished) {
    return <PublishedRollbackDialog {...props} />;
  }
  return <StagedRollbackDialog {...props} />;
}

// --- Staged rollback (unchanged logic, same as before) ---

function StagedRollbackDialog({ target, onRolledBack, onClose }: PcmpUrgentRollbackDialogProps) {
  const hasSelection = target.proposalIds.length > 0;
  const form = useForm<StagedFormValues>({
    resolver: zodResolver(stagedFormSchema),
    defaultValues: { scope: hasSelection ? 'SELECTED' : 'WHOLE', comment: '' },
  });
  const { mutate, isPending, error } = useRollbackPcmpUrgentBatch(target.batchNo, () => {
    onRolledBack();
    onClose();
  });

  const onSubmit = form.handleSubmit((values) => {
    const comment = values.comment === '' ? undefined : values.comment;
    mutate(
      values.scope === 'SELECTED'
        ? { proposalIds: target.proposalIds, comment }
        : { wholeBatch: true, comment },
    );
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Roll back from Urgent Batch #${target.batchNo}`}
        description="Back to Urgent Pre-Batch"
        icon={Undo2}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Roll back"
        submitVariant="destructive"
        pendingLabel="Rolling back..."
      >
        <FormField
          control={form.control}
          name="scope"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Roll back</FormLabel>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange}>
                  <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
                    <RadioGroupItem
                      id="pcmp-urgent-rollback-selected"
                      value="SELECTED"
                      disabled={!hasSelection}
                    />
                    <Label htmlFor="pcmp-urgent-rollback-selected" className="font-normal">
                      {hasSelection
                        ? `The ${formatCount(target.proposalIds.length, 'selected proposal')} (${formatTaka(target.selectedAmount)})`
                        : 'The selected proposals (none ticked)'}
                    </Label>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
                    <RadioGroupItem id="pcmp-urgent-rollback-whole" value="WHOLE" />
                    <Label htmlFor="pcmp-urgent-rollback-whole" className="font-normal">
                      The whole batch ({formatCount(target.wholeCount, 'proposal')})
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <p className="text-xs text-muted-foreground">
                The proposals wait in Urgent Pre-Batch again. The batch keeps its number and stays
                open for new proposals.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          placeholder="Optional note for the timeline"
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}

// --- Published rollback (LDS-823) ---

function PublishedRollbackDialog({
  target,
  refNoOf,
  onRolledBack,
  onClose,
}: PcmpUrgentRollbackDialogProps) {
  const [outcome, setOutcome] = useState<PcmpUrgentRollbackResult | null>(null);

  const form = useForm<PublishedFormValues>({
    resolver: zodResolver(publishedFormSchema),
    defaultValues: { paymentSource: 'MARKETING', reason: '', comment: '' },
  });

  const { mutate, isPending, error } = useRollbackPcmpUrgentBatch(target.batchNo, (result) => {
    onRolledBack();
    if (result.blocked && result.blocked.length > 0) setOutcome(result);
    else onClose();
  });

  const onSubmit = form.handleSubmit((values) => {
    const comment = values.reason
      ? `[${ROLLBACK_REASONS.find((r) => r.value === values.reason)?.label ?? values.reason}] ${values.comment}`
      : values.comment;
    mutate({
      proposalIds: target.proposalIds,
      paymentSource: values.paymentSource,
      comment,
    });
  });

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title="Rolled back"
        summary={`${formatCount(outcome.rolledBack.length, 'proposal')} moved to ${
          outcome.paymentSource ? PCMP_PRE_BATCH_LABELS[outcome.paymentSource] : 'Pre-Batch'
        }. The ones below could not be rolled back.`}
        skipped={outcome.blocked ?? []}
        refNoOf={refNoOf}
        onClose={onClose}
      />
    );
  }

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Roll back from Urgent Batch #${target.batchNo}`}
        description={`${formatCount(target.proposalIds.length, 'published proposal')} · ${formatTaka(target.selectedAmount)}`}
        icon={Undo2}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Roll back"
        submitVariant="destructive"
        pendingLabel="Rolling back..."
      >
        <FormField
          control={form.control}
          name="paymentSource"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Roll back to <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange}>
                  {PCMP_CHANNELS.map((channel) => (
                    <div
                      key={channel}
                      className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                    >
                      <RadioGroupItem id={`pcmp-urgent-pub-rollback-${channel}`} value={channel} />
                      <Label
                        htmlFor={`pcmp-urgent-pub-rollback-${channel}`}
                        className="font-normal"
                      >
                        {PCMP_PRE_BATCH_LABELS[channel]}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <p className="text-xs text-muted-foreground">
                The payment source changes to match, and the proposals wait on that MIS staging tab.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Reason <span className="text-destructive">*</span>
              </FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a reason" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ROLLBACK_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Why these proposals leave the batch"
        />
      </PcmpEditDialog>
    </Form>
  );
}
